import { ClickAwayListener, Popper } from "@mui/base";
import { SchemaField } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import {
  variableDisplayName,
  variableTypeName,
} from "@redotech/redo-flows-core-schema-common/type-system/variable-display";
import { memo, useEffect, useRef } from "react";
import { RedoBadge } from "./arbiter-components/badge/redo-badge";
import { Flex } from "./flex";
import * as inlineVariableMenuCss from "./inline-variable-menu.module.css";
import { Text } from "./text";

/**
 * The menu a text surface shows while a variable is being typed. The anchor is
 * a caret rect in the rich-text editors and the field itself in plain inputs.
 */
export const InlineVariableMenu = memo(function InlineVariableMenu({
  anchor,
  fields,
  activeIndex,
  onActiveIndexChange,
  onSelect,
  onClickAway,
}: {
  anchor: HTMLElement | { getBoundingClientRect: () => DOMRect } | null;
  fields: readonly SchemaField[];
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onSelect: (field: SchemaField) => void;
  onClickAway: () => void;
}) {
  const activeRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (!anchor) {
    return null;
  }

  return (
    <Popper
      anchorEl={anchor}
      modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
      open
      placement="bottom-start"
    >
      <ClickAwayListener onClickAway={onClickAway}>
        {/* Marks a floating overlay, so builders that deselect on outside-click
            ignore clicks on this portaled menu. */}
        <Flex
          bgColor="primary"
          borderColor="primary"
          borderRadius="lg"
          borderWidth="1px"
          className={inlineVariableMenuCss.menu}
          data-portal-selector="redo-dropdown"
          dir="column"
          gap="none"
          maxHeight="quarter-screen"
          overflowY="auto"
          p="xs"
          shadow="lg"
        >
          <Flex px="sm" py="xs">
            <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
              Insert a variable
            </Text>
          </Flex>
          {fields.length === 0 ? (
            <Flex px="sm" py="xs">
              <Text fontSize="sm" fontStyle="italic" textColor="placeholder">
                No matching variables
              </Text>
            </Flex>
          ) : (
            fields.map((field, index) => (
              <Flex
                align="center"
                as="button"
                bgColor={index === activeIndex ? "secondary" : "none"}
                borderRadius="md"
                borderWidth="none"
                cursor="pointer"
                key={field.key}
                // Keep the surface's selection intact until the caret is read.
                onMouseDown={(event) => {
                  event.preventDefault();
                  onSelect(field);
                }}
                onMouseEnter={() => onActiveIndexChange(index)}
                p="sm"
                ref={index === activeIndex ? activeRef : undefined}
                type="button"
                w="full"
              >
                <Flex
                  align="flex-start"
                  dir="column"
                  gap="none"
                  style={{ minWidth: 0 }}
                  w="full"
                >
                  <Flex
                    align="center"
                    gap="xs"
                    justify="space-between"
                    w="full"
                  >
                    <Text fontSize="sm" textColor="primary">
                      {variableDisplayName(field)}
                    </Text>
                    {variableTypeName(field.dataType) && (
                      <RedoBadge
                        color="gray"
                        size="xs"
                        text={variableTypeName(field.dataType)!} // eslint-disable-line @typescript-eslint/no-non-null-assertion
                      />
                    )}
                  </Flex>
                  {field.documentation && (
                    <Text
                      className={inlineVariableMenuCss.documentation}
                      fontSize="xs"
                      textAlign="left"
                      textColor="secondary"
                    >
                      {field.documentation}
                    </Text>
                  )}
                </Flex>
              </Flex>
            ))
          )}
        </Flex>
      </ClickAwayListener>
    </Popper>
  );
});
